// One customer's AWS costs for one month (SCR-012, API-036): the actual AWS bill matched to its
// metered usage per account, day, model and token class (allocated, unattributed, credits,
// refunds, unmapped rows), and Finalize with force and a required reason. A finalized month shows
// its frozen, read-only snapshot with its digest, and an approver can restate it when AWS changed
// the month's Cost and Usage Report afterwards.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { buildPath, RouterContext } from "../../router/router";
import { SessionContext } from "../../session/session";
import { useCustomerName } from "../../ui/customer-directory";
import { Dialog } from "@nexus/ui";
import { Checkbox, errorId, Field } from "@nexus/ui";
import { actorDisplay, actorTitle, knownActor } from "../../ui/identity";
import { MonthPicker } from "@nexus/ui";
import { currentMonth, longMonthLabel } from "@nexus/ui";
import { Card, Chip, CopyId, EmptyState, NameCell, PageHeader, StatCard, StatGrid } from "@nexus/ui";
import { useBreadcrumb } from "../../ui/shell";
import { ErrorBanner, Loading } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import {
  BASIS_KIND_LABELS,
  EXPENSE_PATH,
  EXPENSE_PERIOD_PATH,
  TOKEN_CLASS_LABELS,
  expensePeriodView,
  finalizeRefusalFrom,
  finalizeRequestBody,
  loadExpensePeriod,
  restateRefusalFrom,
  validateFinalizeRequest,
  type ExpenseReconciliation,
  type ExpenseSnapshot,
  type FinalizeRefusal,
  type ReconciliationAccountView,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

/** A finalize or restate refusal (API-036), with what the API named and whether force helps. */
function RefusalBanner({ refusal }: { refusal: FinalizeRefusal }) {
  return (
    <div class="banner banner-error" role="alert">
      <p>
        <code class="error-code">{refusal.code}</code> {refusal.message}
      </p>
      {refusal.details.length > 0 && (
        <ul>
          {refusal.details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
      {refusal.forcible && <p>Tick “Force” below and give a reason to go ahead anyway.</p>}
      {refusal.retryable && !refusal.forcible && <p>This should clear on its own. Try again shortly.</p>}
    </div>
  );
}

export function ExpensePeriodScreen({ params }: ScreenProps<typeof EXPENSE_PERIOD_PATH>) {
  const client = useApi();
  return <ExpensePeriod key={`${params.orgId}/${params.period}`} client={client} orgId={params.orgId} period={params.period} />;
}

type ItemRow = ReconciliationAccountView["items"][number];
type UnmappedRow = ReconciliationAccountView["unmapped"][number];

const ITEM_COLUMNS: Column<ItemRow>[] = [
  { key: "day", header: "Day", cell: (item) => item.day, compare: (a, b) => a.day.localeCompare(b.day) },
  { key: "model", header: "Model", cell: (item) => <span class="mono">{item.bedrockModelId}</span> },
  { key: "class", header: "Token class", help: "finance.tokenClass", cell: (item) => TOKEN_CLASS_LABELS[item.tokenClass] },
  { key: "curNet", header: "CUR net", numeric: true, help: "finance.curNet", cell: (item) => <Money micro={item.curNetMicroUsd} /> },
  { key: "allocated", header: "Allocated", numeric: true, help: "finance.allocated", cell: (item) => <Money micro={item.allocatedMicroUsd} /> },
  { key: "unattributed", header: "Unattributed", numeric: true, help: "finance.unattributed", cell: (item) => <Money micro={item.unattributedMicroUsd} /> },
  {
    key: "listCost",
    header: "List cost",
    numeric: true,
    help: "finance.listCost",
    cell: (item) => (
      <span class="cell-cost">
        <Money micro={item.listCostMicroUsd} />
      </span>
    ),
  },
];

const UNMAPPED_COLUMNS: Column<UnmappedRow>[] = [
  { key: "productCode", header: "Product code", help: "finance.productCode", cell: (row) => row.productCode },
  { key: "usageType", header: "Usage type", cell: (row) => <span class="mono">{row.usageType}</span> },
  { key: "rows", header: "Rows", numeric: true, cell: (row) => row.rows.toLocaleString() },
  { key: "net", header: "Net", numeric: true, cell: (row) => <Money micro={row.netMicroUsd} /> },
];

function ExpensePeriod({ client, orgId, period }: { client: ApiClient; orgId: string; period: string }) {
  const services = useContext(SessionContext);
  const router = useContext(RouterContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canApprove = session?.can("billing:approve") ?? false;
  const name = useCustomerName(orgId);
  useBreadcrumb([{ label: name, href: EXPENSE_PATH }, { label: longMonthLabel(period) }]);

  const [reconciliation, setReconciliation] = useState<ExpenseReconciliation | null>(null);
  const [snapshot, setSnapshot] = useState<ExpenseSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [dialog, setDialog] = useState<"finalize" | "restate" | null>(null);
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    loadExpensePeriod(client, orgId, period)
      .then((data) => {
        setSnapshot(data.snapshot);
        setReconciliation(data.reconciliation);
      })
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [orgId, period]);

  const view = expensePeriodView({ loading, error, snapshot, reconciliation });
  const reasonErrors = validateFinalizeRequest({ force, reason });

  const open = (which: "finalize" | "restate") => {
    setForce(false);
    setReason("");
    setActionError(null);
    setDialog(which);
  };

  const submit = () => {
    if (Object.keys(reasonErrors).length > 0 || !dialog) return;
    setWorking(true);
    setActionError(null);
    const body = finalizeRequestBody({ force, reason });
    const request =
      dialog === "finalize"
        ? client.post("/v1/admin/orgs/{orgId}/billing-periods/{period}/expense/finalize", { path: { orgId, period }, body })
        : client.post("/v1/admin/orgs/{orgId}/billing-periods/{period}/expense/restate", { path: { orgId, period }, body });
    request
      .then((result) => {
        setSnapshot(result);
        setDialog(null);
      })
      // A refusal (`cur_unmapped`, `restatement_held`, …) keeps the dialog open with its own banner,
      // so the force and reason the operator was about to retry with are not lost.
      .catch((err: unknown) => setActionError(asApiError(err)))
      .finally(() => setWorking(false));
  };

  const goToMonth = (next: string) => router?.navigate(buildPath(EXPENSE_PERIOD_PATH, { orgId, period: next }));

  const status =
    view.kind === "finalized" ? (
      <Chip tone="green">Finalized</Chip>
    ) : view.kind === "reconciling" ? (
      view.summary.allFinal && !view.summary.hasUnmapped ? (
        <Chip tone="blue">Ready to finalize</Chip>
      ) : (
        <Chip tone="amber">Not ready</Chip>
      )
    ) : null;

  const header = (
    <PageHeader
      title={`AWS costs for ${longMonthLabel(period)}`}
      titleExtra={status}
      lede="The actual AWS bill for this customer's month, matched to its metered usage. Finalizing freezes the month's AWS cost."
      meta={<NameCell name={name} id={orgId} />}
      actions={
        <Field id="expense-month" label="Month" help="common.month" compact>
          <MonthPicker id="expense-month" value={period} onChange={goToMonth} max={currentMonth(new Date())} />
        </Field>
      }
    />
  );

  if (view.kind === "loading")
    return (
      <div class="page">
        {header}
        <Loading label="Loading the month…" />
      </div>
    );
  if (view.kind === "error")
    return (
      <div class="page">
        {header}
        <ErrorBanner error={view.error} onRetry={load} />
      </div>
    );

  const refusal = actionError ? (dialog === "restate" ? restateRefusalFrom(actionError) : finalizeRefusalFrom(actionError)) : null;

  return (
    <div class="page expense-period-screen">
      {header}

      {view.kind === "empty" && (
        <EmptyState
          title="No AWS account to read yet"
          lede="Link an AWS account first. This page compares the customer's actual AWS bill with its metered usage, so it needs an AWS account linked with the CUR capability and a verified CUR source."
          steps={[
            { title: "Link the account", text: "An installer operator links the customer's AWS account with the CUR capability." },
            { title: "Add a CUR source", text: "On AWS costs, pick the customer and add the export the bootstrap stack created, then Verify it." },
            { title: "Come back after month end", text: "AWS delivers each month's final report within about a day of month end." },
          ]}
        />
      )}

      {view.kind === "finalized" && (
        <>
          <StatGrid label="Frozen AWS cost">
            <StatCard label="AWS cost" help="common.awsCost" value={<Money micro={view.snapshot.expenseMicroUsd} />} note={BASIS_KIND_LABELS[view.snapshot.basis]} />
            <StatCard label="Unattributed" help="finance.unattributed" value={<Money micro={view.snapshot.unattributedMicroUsd} />} />
            <StatCard label="Credits" value={<Money micro={view.snapshot.creditsMicroUsd} />} />
            <StatCard label="Refunds" value={<Money micro={view.snapshot.refundsMicroUsd} />} />
          </StatGrid>
          <Card
            title="Snapshot"
            actions={
              canApprove && (
                <button type="button" class="button" onClick={() => open("restate")}>
                  Restate…
                </button>
              )
            }
          >
            <dl class="detail-list">
              <dt>Finalized</dt>
              <dd>
                <span title={actorTitle(view.snapshot.finalizedBy, knownActor(view.snapshot, "finalizedBy"))}>{actorDisplay(view.snapshot.finalizedBy, session?.me, knownActor(view.snapshot, "finalizedBy"))}</span> on{" "}
                {new Date(view.snapshot.finalizedAt).toLocaleString()}
              </dd>
              <dt>Revision</dt>
              <dd>{view.snapshot.revision ?? 1}</dd>
              {view.snapshot.forced && (
                <>
                  <dt>Forced</dt>
                  <dd>{view.snapshot.forceReason}</dd>
                </>
              )}
              <dt>Snapshot digest</dt>
              <dd>
                <CopyId id={view.snapshot.snapshotSha256} label="" what="digest" />
              </dd>
            </dl>
          </Card>
        </>
      )}

      {view.kind === "reconciling" && (
        <>
          {view.summary.hasUnmapped && (
            <div class="banner banner-error" role="alert">
              <p>Some usage rows with cost have no verified usage-type mapping. Map them on AWS costs before finalizing, or force with a reason.</p>
            </div>
          )}
          {!view.summary.allFinal && (
            <div class="banner banner-warning" role="alert">
              <p>
                AWS has not marked this month final yet for account {view.summary.pendingAccountIds.join(", ")}. AWS delivers each month's final report within
                about 24 hours of month end.
              </p>
            </div>
          )}
          {view.summary.accounts.map((account) => (
            <Card
              key={account.awsAccountId}
              title={`AWS account ${account.awsAccountId}`}
              actions={account.final ? <Chip tone="green">Final</Chip> : <Chip tone="amber">Pending</Chip>}
            >
              <p class="card-note">
                Credits <Money micro={account.creditsMicroUsd} /> · Refunds <Money micro={account.refundsMicroUsd} />
                {account.overMeteredDays > 0 && ` · ${account.overMeteredDays} ${account.overMeteredDays === 1 ? "cell" : "cells"} where the proxy metered more than AWS billed`}
              </p>
              <DataTable
                caption={`Allocation per day, model and token class — account ${account.awsAccountId}`}
                hideCaption
                columns={ITEM_COLUMNS}
                rows={account.items}
                rowKey={(item) => `${item.day}/${item.bedrockModelId}/${item.tokenClass}`}
                emptyMessage="No Bedrock cost in this account's report for the month."
              />
              {account.unmapped.length > 0 && (
                <DataTable
                  caption={`Unmapped usage — account ${account.awsAccountId}`}
                  columns={UNMAPPED_COLUMNS}
                  rows={account.unmapped}
                  rowKey={(row) => `${row.productCode}/${row.usageType}`}
                  emptyMessage="No unmapped usage."
                />
              )}
            </Card>
          ))}
          {canApprove && (
            <div class="form-actions">
              <button type="button" class="button button-primary" onClick={() => open("finalize")}>
                Finalize…
              </button>
            </div>
          )}
        </>
      )}

      <Dialog
        title={dialog === "restate" ? `Restate ${longMonthLabel(period)}` : `Finalize ${longMonthLabel(period)}`}
        open={dialog !== null}
        onClose={() => setDialog(null)}
        actions={
          <>
            <button type="button" class="button button-secondary" onClick={() => setDialog(null)}>
              Cancel
            </button>
            <button type="button" class="button button-primary" disabled={working || Object.keys(reasonErrors).length > 0} onClick={submit}>
              {working ? "Working…" : dialog === "restate" ? "Restate" : "Finalize"}
            </button>
          </>
        }
      >
        <div class="stack-sm">
          {dialog === "restate" ? (
            <p>
              Appends a new revision from the month's current Cost and Usage Report. The next invoice carries the difference as late lines; the finalized snapshot
              and written invoices never change. Never restate during a rollout.
            </p>
          ) : (
            <p>This freezes {name}'s AWS cost for the month. It cannot be undone; later changes by AWS are handled by restating.</p>
          )}
          {refusal && <RefusalBanner refusal={refusal} />}
          <Checkbox
            id="finalize-force"
            label={dialog === "restate" ? "Force past a held restatement" : "Force past a pending month, unmapped rows or a basis mismatch"}
            labelText="Force"
            help={dialog === "restate" ? "finance.forceRestate" : "finance.forceFinalize"}
            checked={force}
            onChange={setForce}
          />
          {force && (
            <Field id="finalize-reason" label="Reason" help="finance.forceReason" hint="10 to 500 characters." error={reasonErrors.reason}>
              <textarea
                id="finalize-reason"
                class="input"
                rows={3}
                value={reason}
                aria-describedby={reasonErrors.reason ? errorId("finalize-reason") : undefined}
                onInput={(e) => setReason(e.currentTarget.value)}
              />
            </Field>
          )}
        </div>
      </Dialog>
    </div>
  );
}
